import React, { useEffect, useRef, useState } from "react";
import { Tabs, TabsProps, Tooltip, Modal, Button } from "antd";

import OwnedCar from "./InfoCar/OwnedCar";
import UsedCar from "./InfoCar/UsedCar";
import InterestedCar from "./InfoCar/InterestedCar";
import { QuestionCircleOutlined, SettingOutlined } from "@ant-design/icons";
import CustomerFeilds from "../../CustomersV2/Drawer/CustomerFeilds";
import { FIELDTYPE } from "../../../services/data";
import { getListCustomerVehicle, getListFieldsByCustomer, ICustomer, IField, ListField } from "../../../api/Customer_V2/customerApi_V2";
import useToast from "../../../hook/useToast";
import useLoading from "../../../hook/useLoading";
import { FlattenedField } from "../../CustomersV2/indexV2";
import { useSelector } from "react-redux";
import { selectorInfoUser } from "../../../store/slices/infoUserSlice";
import { ICustomerDetail_V4 } from "../../../api/customer/customerApi";
import CarFeilds from "./InfoCar/CarFeilds";
import { IDataDefaulFilterList } from "./VehicleVersionTable/Modal/FilterHeaderVehicleVersionTable";
import { selectorPermission } from "../../../store/slices/permissionSlice";
import { selectorCategoryCarIds } from "../../../store/slices/categoryCarSlice";

const FIELD_TYPE_MAP = {
  owned: FIELDTYPE.OWNED_VEHICLE,
  used: FIELDTYPE.USING_VEHICLE,
  favorite: FIELDTYPE.INTERESTED_VEHICLE,
} as const;
type VehicleKey = keyof typeof FIELD_TYPE_MAP;

export interface IListVehicles {
  owned: ICustomer[];
  used: ICustomer[];
  favorite: ICustomer[];
}

export interface IDataFilterActive {
  owned: FlattenedField[];
  used: FlattenedField[];
  favorite: FlattenedField[];
}

export interface IDataFilterNoActive {
  owned: ListField[];
  used: ListField[];
  favorite: ListField[];
}

const EMPTY_GROUP = {
  owned: [],
  used: [],
  favorite: [],
};

const EXCLUDED_FIELD_CODES = new Set<string>(["own_vehicle_name",]);

type DataFilterList = Record<VehicleKey, IDataDefaulFilterList>;

const dataDefaulFilter: IDataDefaulFilterList = {
  search: undefined,
  status: undefined,
}
const dataDefaulFilterList: DataFilterList = {
  owned: dataDefaulFilter,
  used: dataDefaulFilter,
  favorite: dataDefaulFilter,
};



interface Props {
  infoCustomer?: ICustomerDetail_V4;

}


const VehicleInformation: React.FC<Props> = ({
  infoCustomer,
}) => {
  const pushToast = useToast();
  const pushLoading = useLoading()
  const { unitTemp } = useSelector(selectorInfoUser)

  const permittedCategoryCarIds = useSelector(selectorCategoryCarIds)


  useEffect(() => {
    gListFieldsActiveAndNotAtive()
  }, [])

  const isFirstLoadRef = useRef(true);
  const [isSettingShow, setSettingShow] = useState(false)
  const [isModalUpdateVehicleInformation, setIsModalUpdateVehicleInformation] = useState(false)
  const [loadingTable, setLoadingTable] = useState(false)
  const [activeInfoTab, setActiveInfoTab] = useState<VehicleKey>("owned");
  const [isModalGuideOpen, setIsModalGuideOpen] = useState(false);

  const [dataFilterActive, setDataFilterActive] = useState<IDataFilterActive>(EMPTY_GROUP);
  const [dataFilterNoActive, setDataFilterNoActive] = useState<IDataFilterNoActive>(EMPTY_GROUP);
  const [dataFilterList, setDataFilterList] = useState<DataFilterList>(dataDefaulFilterList)

  const [listvehicles, setListVehicles] = useState<IListVehicles>(EMPTY_GROUP);

  const filterFields = (fields: IField[]) =>
    fields.filter(field => !EXCLUDED_FIELD_CODES.has(field.fieldCode));

  const fetchFieldsByStatus = async (active: 0 | 1) => {
    pushLoading(true)
    const requests = Object.entries(FIELD_TYPE_MAP).map(
      async ([key, type]) => ({
        key: key as VehicleKey,
        res: await getListFieldsByCustomer(type, active),
      })
    );

    const responses = await Promise.all(requests);

    return responses.reduce<Record<VehicleKey, any[]>>((acc, { key, res }) => {
      if (!res?.status) {
        acc[key] = [];
        return acc;
      }

      acc[key] = active
        ? res.data.flatMap((choice: ListField) =>
          filterFields(choice.fields).map(field => ({
            ...field,
            parentId: choice.id,
          }))
        )
        : res.data.map((choice: ListField) => ({
          ...choice,
          fields: filterFields(choice.fields),
        }));
      pushLoading(false)
      return acc;
    }, {} as Record<VehicleKey, any[]>);

  };

  const gListFieldsActiveAndNotAtive = async () => {
    try {
      setLoadingTable(true);

      const activeData = await fetchFieldsByStatus(1);
      setDataFilterActive(activeData);

      const inactiveData = await fetchFieldsByStatus(0);
      setDataFilterNoActive(inactiveData);

    } finally {
      setLoadingTable(false);
    }
  };

  const gListAllCustomerVehicles = async () => {
    if (!infoCustomer?.id) {
      setListVehicles(EMPTY_GROUP);
      return;
    }
    setLoadingTable(true)
    const requests = (Object.entries(FIELD_TYPE_MAP) as [VehicleKey, number][])
      .map(async ([key, type]) => {
        const res = await getListCustomerVehicle(
          infoCustomer.id, type,
          unitTemp, permittedCategoryCarIds
        );
        return { key, res };
      });

    const responses = await Promise.all(requests);

    const result = responses.reduce((acc, { key, res }) => {
      acc[key] = res?.status ? res.data : [];
      return acc;
    }, { ...EMPTY_GROUP } as IListVehicles);

    setListVehicles(result);
    setLoadingTable(false)
  };

  const gListCustomerVehicles = async () => {
    if (!infoCustomer?.id) {
      setListVehicles(EMPTY_GROUP);
      return;
    }

    setLoadingTable(true);
    try {
      const type = FIELD_TYPE_MAP[activeInfoTab];
      const { status } = dataFilterList[activeInfoTab]

      const res = await getListCustomerVehicle(
        infoCustomer.id, type,
        unitTemp, permittedCategoryCarIds, status
      );

      setListVehicles(prev => ({
        ...prev,
        [activeInfoTab]: res?.status ? res.data : [],
      }));

    } finally {
      setLoadingTable(false);
    }
  };

  const onSaveDataFields = (iSsuccess?: boolean, message?: string, dataFilterActive?: FlattenedField[], dataFilterNoActive?: ListField[]
  ) => {
    if (iSsuccess) {
      pushToast(message, "success")
      setSettingShow(false)

      if (dataFilterActive && dataFilterNoActive) {
        setDataFilterActive(prev => ({
          ...prev,
          [activeInfoTab]: dataFilterActive ?? [],
        }));

        setDataFilterNoActive(prev => ({
          ...prev,
          [activeInfoTab]: dataFilterNoActive ?? [],
        }));
      }

    } else {
      pushToast(message, "warning")
    }
  };

  const filterDataListCustomerVehicles = (data: IDataDefaulFilterList) => {
    setDataFilterList(prev => ({
      ...prev,
      [activeInfoTab]: data,
    }));
  }

  const resultFormSubmitOwnedCarOrUsedCar = (isSucces: boolean, message?: string) => {
    if (isSucces) {
      pushToast(message, "success")
      gListCustomerVehicles()
      setIsModalUpdateVehicleInformation(false)
    } else {
      pushToast(message, "warning")
    }
  }

  useEffect(() => {
    if (!infoCustomer?.id) return;
    gListAllCustomerVehicles();
  }, [infoCustomer?.id, dataFilterList]);

  return (
    <>
      {
        isSettingShow ?
          <CarFeilds
            isSettingShow={isSettingShow}
            recordId={FIELD_TYPE_MAP[activeInfoTab]}
            type={0}
            dataFilterActive={dataFilterActive[activeInfoTab]}
            dataFilterNoActive={dataFilterNoActive[activeInfoTab]}

            onSaveDataFields={onSaveDataFields}
            onClose={() => setSettingShow(false)}
          /> :
          null
      }
      <div
        style={{ width: "100%" }}
        className="custom-page-css relative"
      >
        <Tabs
          activeKey={activeInfoTab}
          items={
            [
              {
                key: "owned",
                label: (
                  <span className="custom-page-css font-light text-lg">
                    Xe sở hữu ({listvehicles["owned"]?.length || 0})
                  </span>
                ),
                children:
                  <OwnedCar
                    loadingTable={loadingTable}
                    isModalUpdateVehicleInformation={isModalUpdateVehicleInformation}
                    dataFilterActive={dataFilterActive["owned"]}
                    dataFilterList={dataFilterList["owned"]}

                    idCustomer={infoCustomer?.id}
                    listVehicle={listvehicles["owned"]}

                    setDataFilterList={filterDataListCustomerVehicles}
                    setIsModalUpdateVehicleInformation={setIsModalUpdateVehicleInformation}
                    resultFormSubmitOwnedCarOrUsedCar={resultFormSubmitOwnedCarOrUsedCar}
                  />
                ,
              },
              {
                key: "used",
                label: (
                  <span className="custom-page-css font-light text-lg">
                    Xe sử dụng ({listvehicles["used"]?.length || 0})
                  </span>
                ),
                children:
                  <UsedCar
                    loadingTable={loadingTable}
                    // isModalUpdateVehicleInformation={isModalUpdateVehicleInformation}

                    dataFilterActive={dataFilterActive["used"]}

                    idCustomer={infoCustomer?.id}
                    listVehicle={listvehicles["used"]}
                  // setIsModalUpdateVehicleInformation={setIsModalUpdateVehicleInformation}

                  />,
              },
              {
                key: "favorite",
                label: (
                  <span className="custom-page-css font-light text-lg">
                    Xe quan tâm ({listvehicles["favorite"]?.length || 0})
                  </span>
                ),
                children:
                  <InterestedCar
                    loadingTable={loadingTable}
                    dataFilterActive={dataFilterActive["favorite"]}

                    idCustomer={infoCustomer?.id}
                    listVehicle={listvehicles["favorite"]}
                  />,
              },
            ]
          }
          onChange={(key) => setActiveInfoTab(key as VehicleKey)}
        />


      </div>
      <div className="custom-page-css flex items-center gap-2 absolute top-0 right-0">
        <div className='settingCol'>
          <div className='ctr-col-setting'
            onClick={() => setIsModalGuideOpen(true)}
          >
            <QuestionCircleOutlined />
          </div>
        </div>

        <div className='settingCol'>
          <div className='ctr-col-setting'
            onClick={() => setSettingShow(true)}
          >
            <SettingOutlined />
          </div>
        </div>
      </div>
      <Modal
        title="Hướng dẫn sử dụng"
        open={isModalGuideOpen}
        onCancel={() => setIsModalGuideOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsModalGuideOpen(false)}>
            Đóng
          </Button>
        ]}
        width={800}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '14px', marginTop: '16px', color: 'rgba(0, 0, 0, 0.85)' }}>
          <div>
            Xe sở hữu: Là các xe mà khách hàng được xác định là chủ sở hữu pháp lý trên hợp đồng mua xe.
          </div>
          <div>
            Xe sử dụng: Là các xe khách hàng đang trực tiếp sử dụng nhưng không nhất thiết là chủ sở hữu pháp lý hoặc các xe chưa cập nhật đầy đủ thông tin hợp đồng trên hệ thống.
            <br />
            <span style={{ textDecoration: 'underline' }}>Ví dụ:</span> Xe công ty cấp cho nhân viên sử dụng, xe người thân đứng tên.
          </div>
          <div>
            Xe quan tâm: Là các xe mà khách hàng đang quan tâm thông qua các hoạt động như tìm hiểu thông tin, yêu cầu tư vấn, báo giá, lái thử... nhưng chưa phát sinh giao dịch mua xe.
          </div>
          <div>
            Người đại diện: Là cá nhân được xác định trên hợp đồng mua xe với vai trò đại diện cho đơn vị, tổ chức.
          </div>
          <div>
            Người liên hệ: Là cá nhân trực tiếp liên hệ và làm việc với đại lý, xưởng dịch vụ liên quan đến xe. Người liên hệ có thể là chủ sở hữu, người sử dụng mang xe đi bảo dưỡng, sửa chữa.
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VehicleInformation;